// @flow
type HoverRegion = 'left' | 'right' | 'center';
import type { HoverRelativePosition } from 'app/common/react/components/DragDrop/DraggableItem';

export type HoverLocation = {
  itemIndex: number,
  regionWithinItem: HoverRegion,
};

const CENTER_SENSITIVITY_DEFAULT = 0.5;

export function hoverLocationsAreEqual(location1: HoverLocation, location2: HoverLocation) {
  return (
    location1.itemIndex === location2.itemIndex &&
    location1.regionWithinItem === location2.regionWithinItem
  );
}

export function getHoverRegionWithinItem({
  hoverRelativePosition: {
    positionRelativeToCenter: { x },
    boundingBox: { width },
  },
  centerEnabled,
}: {
  hoverRelativePosition: HoverRelativePosition,
  centerEnabled: boolean,
}): HoverRegion {
  const centerWidth = centerEnabled ? CENTER_SENSITIVITY_DEFAULT * width : 0;
  if (Math.abs(x) < centerWidth / 2) {
    return 'center';
  }
  return x < 0 ? 'left' : 'right';
}

export function getTargetIndex({
  hoverLocation: { itemIndex: hoverIndex, regionWithinItem: hoverRegionWithItem },
  sourceIndex,
}: {
  hoverLocation: HoverLocation,
  sourceIndex: number,
}) {
  if (hoverRegionWithItem === 'center' || hoverIndex === sourceIndex) {
    return hoverIndex;
  }

  // NOTE(jackhsu): the simple reorderItems logic is:
  // - if the item hovered is located _before_ the source item, dropping the source item will effectively
  //   place the source item _before_ the hovered item.
  // - if the item hovered is located _after_ the source item, dropping the source item will effectively
  //   place the dragged item _after_ the hovered item.
  //   (since the hovered item gets shifted to the left to fill in the hole)
  const insertBefore = hoverIndex < sourceIndex;
  const shouldInsertBefore = hoverRegionWithItem === 'left';

  if (insertBefore === shouldInsertBefore) {
    return hoverIndex;
  }
  // NOTE(jackhsu): adjust target index when simple reordering logic
  // such that we can use the simple reordering
  return shouldInsertBefore ? hoverIndex - 1 : hoverIndex + 1;
}
